import axios from 'axios';
import { getToken } from '../utils/auth';

// Use the ADMIN_PANEL_URL environment variable in production, or fall back to relative URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_URL = `${API_BASE_URL}/backend/metrics`;

// Set up axios interceptor for authentication
axios.interceptors.request.use(
  config => {
    const token = getToken();
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * Get current real-time metrics
 * @returns {Promise<Object>} Current metrics object
 */
export const getCurrentMetrics = async () => {
  try {
    const response = await axios.get(`${API_URL}/current`);
    return response.data;
  } catch (error) {
    console.error('Error fetching current metrics:', error);
    throw error;
  }
};

/**
 * Get historical metrics by period
 * @param {string} period - Period type (hourly, daily, weekly)
 * @param {number} limit - Limit number of records
 * @returns {Promise<Object>} Historical metrics object
 */
export const getHistoricalMetrics = async (period, limit = 24) => {
  try {
    const response = await axios.get(`${API_URL}/historical/${period}?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching historical ${period} metrics:`, error);
    throw error;
  }
};

/**
 * Get project statistics
 * @param {number} limit - Limit number of projects
 * @returns {Promise<Object>} Project stats object
 */
export const getProjectStats = async (limit = 10) => {
  try {
    const response = await axios.get(`${API_URL}/projects?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

/**
 * Get keyword metrics
 * @returns {Promise<Object>} Keyword metrics object
 */
export const getKeywordMetrics = async () => {
  try {
    const response = await axios.get(`${API_URL}/keywords`);
    return response.data;
  } catch (error) {
    console.error('Error fetching keyword metrics:', error);
    throw error;
  }
};

export const getFlowDebugSnapshot = async (params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.limit) {
      searchParams.set('limit', String(params.limit));
    }

    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await axios.get(`${API_URL}/flow${suffix}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching flow debug snapshot:', error);
    throw error;
  }
};

export const getOpsMonitorMetrics = async (params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.windowHours) {
      searchParams.set('windowHours', String(params.windowHours));
    }
    if (params.bucketMinutes) {
      searchParams.set('bucketMinutes', String(params.bucketMinutes));
    }
    if (params.flowWindowHours) {
      searchParams.set('flowWindowHours', String(params.flowWindowHours));
    }
    if (params.flowBucketMinutes) {
      searchParams.set('flowBucketMinutes', String(params.flowBucketMinutes));
    }
    if (params.handlerWindowHours) {
      searchParams.set('handlerWindowHours', String(params.handlerWindowHours));
    }
    if (params.handlerBucketMinutes) {
      searchParams.set('handlerBucketMinutes', String(params.handlerBucketMinutes));
    }
    if (params.hourlyWindowHours) {
      searchParams.set('hourlyWindowHours', String(params.hourlyWindowHours));
    }
    if (params.dailyWindowDays) {
      searchParams.set('dailyWindowDays', String(params.dailyWindowDays));
    }

    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await axios.get(`${API_URL}/ops-monitor${suffix}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching operations monitor metrics:', error);
    throw error;
  }
};

export const traceMessageFlowByLink = async (link) => {
  try {
    const searchParams = new URLSearchParams();
    if (link) {
      searchParams.set('link', String(link));
    }

    const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await axios.get(`${API_URL}/flow/trace-message${suffix}`);
    return response.data;
  } catch (error) {
    console.error('Error tracing message flow by link:', error);
    throw error;
  }
};

export const flushMessageBuffer = async (reason = '') => {
  try {
    const response = await axios.post(`${API_URL}/flow/buffer/flush`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error flushing message buffer:', error);
    throw error;
  }
};

export const sendReportDeliveryTest = async ({ projectId, text } = {}) => {
  try {
    const response = await axios.post(`${API_URL}/flow/test-report-delivery`, {
      projectId,
      text,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending report delivery test:', error);
    throw error;
  }
};

/**
 * Clear metrics cache
 * @returns {Promise<Object>} Response object
 */
export const clearMetricsCache = async () => {
  try {
    const response = await axios.post(`${API_URL}/clear-cache`);
    return response.data;
  } catch (error) {
    console.error('Error clearing metrics cache:', error);
    throw error;
  }
};

/**
 * Manually trigger metrics persistence
 * @param {string} period - Period type (hourly, daily, weekly)
 * @param {boolean} reset - Whether to reset metrics after persistence
 * @returns {Promise<Object>} Response object
 */
export const persistMetrics = async (period = 'hourly', reset = false) => {
  try {
    const response = await axios.post(`${API_URL}/persist`, { period, reset });
    return response.data;
  } catch (error) {
    console.error('Error persisting metrics:', error);
    throw error;
  }
};

/**
 * Get memory monitoring data
 * @param {boolean} detailed - Whether to get detailed memory statistics
 * @returns {Promise<Object>} Memory statistics object
 */
export const getMemoryStats = async (detailed = false) => {
  try {
    const endpoint = detailed ? '/memory?detailed=true' : '/memory';
    const response = await axios.get(`${API_BASE_URL}${endpoint}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching memory stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get health check with memory information
 * @returns {Promise<Object>} Health check object with memory stats
 */
export const getHealthWithMemory = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching health with memory:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Trigger garbage collection
 * @returns {Promise<Object>} GC result object
 */
export const triggerGarbageCollection = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/gc`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error triggering garbage collection:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get memory monitor data
 * @returns {Promise<Object>} Memory monitor data object
 */
export const getMemoryMonitorData = async () => {
  try {
    const response = await axios.get(`${API_URL}/memory-monitor`);
    return response.data;
  } catch (error) {
    console.error('Error fetching memory monitor data:', error);
    throw error;
  }
};

/**
 * Get detailed memory breakdown by component
 * @returns {Promise<Object>} Detailed memory breakdown object
 */
export const getDetailedMemoryBreakdown = async () => {
  try {
    const response = await axios.get(`${API_URL}/memory-detailed`);
    return response.data;
  } catch (error) {
    console.error('Error fetching detailed memory breakdown:', error);
    throw error;
  }
};

/**
 * Run memory diagnostics
 * @returns {Promise<Object>} Memory diagnostics results
 */
export const runMemoryDiagnostics = async () => {
  try {
    const response = await axios.post(`${API_URL}/memory-diagnostics`);
    return response.data;
  } catch (error) {
    console.error('Error running memory diagnostics:', error);
    throw error;
  }
};

export default {
  getCurrentMetrics,
  getHistoricalMetrics,
  getProjectStats,
  getKeywordMetrics,
  getOpsMonitorMetrics,
  getFlowDebugSnapshot,
  traceMessageFlowByLink,
  flushMessageBuffer,
  sendReportDeliveryTest,
  clearMetricsCache,
  persistMetrics,
  getMemoryMonitorData,
  getDetailedMemoryBreakdown,
  runMemoryDiagnostics
};
